/**
 * @file signInFlow.ts
 * @brief The order of a sign-in: first step, then (only on a yes) the panel.
 *
 * @details
 * W27 (DES 26-10-2026): with two-step verification on, the full panel loader
 * ("Signing you in…", held 2.2s by the route guards) showed BEFORE the code
 * screen, as if the person were already in. The cause: the status went to
 * "loading" before the first step was even sent. Now nothing changes on
 * screen until the server answers. A yes opens the panel (its loader starts
 * there); a no or a 2FA answer goes to `onRefused` (keep the challenge, drop
 * any old session and cache) and the page swaps in the code screen. Pure, so
 * `node --test` can load it.
 *
 * @author DES
 * @date 26-10-2026
 */

/** Auth states the route guards cover with the full-page loader. */
export function isAuthPending(status: string): boolean {
  return status === "idle" || status === "loading" || status === "reconnecting";
}

export async function runSignIn<U>(io: {
  /** The first step: password, Google, Apple, Microsoft or a one-tap link. */
  send: () => Promise<unknown>;
  /** The server said yes: the panel loader, then the session. */
  openPanel: (raw: unknown) => Promise<U>;
  /** No, or "a code is needed": runs before the error is rethrown. */
  onRefused: (err: unknown) => void;
}): Promise<U> {
  try {
    return await io.openPanel(await io.send());
  } catch (err) {
    io.onRefused(err);
    throw err;
  }
}
